import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { HttpInternalService } from 'src/app/data/service/http.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { AnalyticsService } from 'src/app/core/service/analytics/analytics.service';

// SD-1137 Dashboard "Finish setup" card. Replaces the previous "Finish setting up
// Synced" panel. Three fixed-order tasks; the card disappears permanently when all
// three are complete (no end state — that would be clutter).
//
// Credit-rewards UI (chip + reward pills) is intentionally absent — see spec §8.
//
// Task 3 ("Process your first bill") completes when AhaMomentReachedAt is set on the
// org (a real bill landed in the queue with extracted data). Clicking install /
// copy / sample-link does NOT complete it — only an actual bill does.

type TaskId = 'workspace' | 'connect_accounting' | 'process_first_bill';

interface AddOn { name: string; appURL: string; logoURL: string; }
interface Platform { name: string; logoURL: string; }

@Component({
  selector: 'app-onboarding-checklist-card',
  templateUrl: './onboarding-checklist-card.component.html',
  styleUrls: ['./onboarding-checklist-card.component.scss']
})
export class OnboardingChecklistCardComponent implements OnInit, OnDestroy {
  @Output() uploadRequested = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  /** Fires on an organisation switch, so a checklist answer for the previous one is dropped. */
  private orgSwitch$ = new Subject<void>();

  // Per-task done state — sourced from backend on init.
  workspaceDone = true;                // Always true after signup; we only render to signed-in users.
  accountingDone = false;
  firstBillDone = false;

  accountingPlatform: 'Xero' | 'QBO' | null = null;
  accountingCompanyName: string | null = null;

  forwardingEmail = '';
  gmailAddOn: AddOn | null = null;
  outlookAddOn: AddOn | null = null;
  xeroPlatform: Platform | null = null;
  qboPlatform: Platform | null = null;

  loading = true;
  loadError = false;

  // Single-expansion state. null = all collapsed. Workspace task is never expandable.
  expandedTask: Exclude<TaskId, 'workspace'> | null = null;

  accountingConnecting = false;
  copied = false;
  // Per-session dismissal; persists in sessionStorage so a refresh keeps it hidden,
  // but next login surfaces the card again if tasks are still incomplete.
  dismissed = false;
  private viewedFired = false;

  // Synced Go mobile app modal. URLs and QR codes render via qrserver.com
  // (free, no key) so there's no new dependency.
  goModalOpen = false;
  readonly iosAppUrl = 'https://apps.apple.com/app/synced/id1234567890';
  readonly androidAppUrl = 'https://play.google.com/store/apps/details?id=com.syncedhq.app';
  get iosQrUrl(): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=170x170&margin=2&data=${encodeURIComponent(this.iosAppUrl)}`;
  }
  get androidQrUrl(): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=170x170&margin=2&data=${encodeURIComponent(this.androidAppUrl)}`;
  }

  constructor(
    private http: HttpInternalService,
    private organizationService: OrganizationService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    // The card outlives an organisation switch (the dashboard stays mounted), and every piece of
    // its state -- the checklist answer, the dismissal, the accounting skip -- is per organisation.
    // selectedOrganizationId is a BehaviorSubject, so this also does the first load; the setter
    // writes localStorage before it emits, which is what refresh() and the storage keys read.
    this.organizationService.selectedOrganizationId
      .pipe(
        map(() => localStorage.getItem('SelectedOrganization')),
        distinctUntilChanged(),
        takeUntil(this.destroy$))
      .subscribe(() => this.loadForOrganisation());
    // SD-1137: every ingestion site stamps AhaMomentReachedAt at upload time
    // (InvoicesController.UploadDocument / UploadInvoice / UploadInvoiceByOutlook
    // / Gmail+Outlook webhooks / email forwarding helpers / Xero callback). So
    // the FE just needs a cheap nudge to re-fetch on realistic recheck moments:
    // a post-action refresh and a window-focus refresh. No background polling.
    window.addEventListener('focus', this.onFocus);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('focus', this.onFocus);
    this.clearRefreshPoll();
  }

  private onFocus = () => {
    if (!this.firstBillDone) this.refresh();
  };

  private refreshPollTimer: any = null;
  private clearRefreshPoll(): void {
    if (this.refreshPollTimer) {
      clearInterval(this.refreshPollTimer);
      this.refreshPollTimer = null;
    }
  }

  // Poll GetSetupChecklistState every 3s for up to 60s while Task 3 is
  // incomplete and an upload has just been triggered. Stops as soon as we see
  // firstBillProcessed flip to true.
  private pollForFirstBill(): void {
    this.clearRefreshPoll();
    const startedAt = Date.now();
    this.refreshPollTimer = setInterval(() => {
      if (this.firstBillDone || Date.now() - startedAt > 60_000) {
        this.clearRefreshPoll();
        return;
      }
      this.refresh();
    }, 3000);
  }


  private get dismissKey(): string {
    const orgId = localStorage.getItem('SelectedOrganization') || 'anon';
    return `sd1137:setup-checklist:dismissed:${orgId}`;
  }

  // SD-1137: "I don't have an accounting integration" — persistent opt-out,
  // stored per-org in localStorage (not sessionStorage) so it survives across
  // sessions. The task renders as completed (strikethrough + tick) rather
  // than vanishing, so the checklist shape stays consistent.
  private get accountingSkipKey(): string {
    const orgId = localStorage.getItem('SelectedOrganization') || 'anon';
    return `sd1137:setup-checklist:accounting-skipped:${orgId}`;
  }

  get accountingSkipped(): boolean {
    return localStorage.getItem(this.accountingSkipKey) === '1';
  }

  // Unified "this task is settled" — either connected for real, or the user
  // explicitly said they don't have an integration. Used for visibility,
  // strikethrough styling, and the all-done completion stamp.
  get accountingSectionDone(): boolean {
    return this.accountingDone || this.accountingSkipped;
  }

  skipAccounting(): void {
    localStorage.setItem(this.accountingSkipKey, '1');
    // Collapse the row if it was the one expanded.
    if (this.expandedTask === 'connect_accounting') {
      this.expandedTask = null;
    }
    this.analytics.capture('setup_accounting_skipped');
  }

  /** Starts over for the organisation now selected, dropping anything asked of the previous one. */
  private loadForOrganisation(): void {
    this.orgSwitch$.next();
    this.clearRefreshPoll();
    this.dismissed = sessionStorage.getItem(this.dismissKey) === '1';
    this.expandedTask = null;
    this.loading = true;
    this.loadError = false;
    this.refresh();
  }

  private refresh(): void {
    const orgId = localStorage.getItem('SelectedOrganization');
    if (!orgId) {
      this.loading = false;
      return;
    }
    this.http.getRequest<any>('Account/GetSetupChecklistState', { organizationId: orgId })
      .pipe(takeUntil(this.orgSwitch$), takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          const d = res?.data || {};
          this.accountingDone = !!d.accountingConnected;
          this.firstBillDone  = !!d.firstBillProcessed;
          this.accountingPlatform = d.accountingPlatform || null;
          this.accountingCompanyName = d.accountingCompanyName || null;
          this.forwardingEmail = d.forwardingEmail || '';

          const addOns: AddOn[] = d.addOns || [];
          this.gmailAddOn   = addOns.find(a => /gmail/i.test(a?.name)) || null;
          this.outlookAddOn = addOns.find(a => /outlook/i.test(a?.name)) || null;

          const platforms: Platform[] = d.platforms || [];
          this.xeroPlatform = platforms.find(p => /^xero$/i.test(p?.name)) || null;
          this.qboPlatform  = platforms.find(p => /^qbo$/i.test(p?.name)) || null;

          this.loading = false;
          this.fireViewed();
        },
        () => { this.loading = false; this.loadError = true; }
      );
  }

  private fireViewed(): void {
    if (this.viewedFired || !this.visible) return;
    this.viewedFired = true;
    const incomplete = (this.accountingDone ? 0 : 1) + (this.firstBillDone ? 0 : 1);
    this.analytics.capture('setup_checklist_viewed', {
      tasks_complete_count: 3 - incomplete,
      tasks_incomplete_count: incomplete
    });
  }

  // Card is hidden when:
  //  - user has dismissed it this session
  //  - every task is settled (done or skipped)
  //  - we haven't loaded state yet
  get visible(): boolean {
    if (this.dismissed) return false;
    if (this.loading) return false;
    const allSettled = this.accountingSectionDone && this.firstBillDone;
    return !allSettled;
  }

  get singleStepRemaining(): boolean {
    const incomplete = (this.accountingSectionDone ? 0 : 1) + (this.firstBillDone ? 0 : 1);
    return incomplete === 1;
  }

  // ─── Expand / collapse ────────────────────────────────────────────────

  toggle(task: Exclude<TaskId, 'workspace'>): void {
    if (this.isDone(task)) return;
    if (this.expandedTask === task) {
      this.expandedTask = null;
      this.analytics.capture('setup_task_collapsed', { task_id: task });
    } else {
      this.expandedTask = task;
      this.analytics.capture('setup_task_expanded', { task_id: task });
    }
  }

  isExpanded(task: Exclude<TaskId, 'workspace'>): boolean {
    return this.expandedTask === task;
  }

  isDone(task: TaskId): boolean {
    if (task === 'workspace') return this.workspaceDone;
    // Accounting counts as done whether it was really connected OR the user
    // opted out with "I don't have an accounting integration".
    if (task === 'connect_accounting') return this.accountingSectionDone;
    return this.firstBillDone;
  }

  // ─── Dismiss ─────────────────────────────────────────────────────────

  dismiss(): void {
    sessionStorage.setItem(this.dismissKey, '1');
    this.dismissed = true;
    const complete = (this.workspaceDone ? 1 : 0)
                   + (this.accountingDone ? 1 : 0)
                   + (this.firstBillDone ? 1 : 0);
    this.analytics.capture('setup_checklist_dismissed', { tasks_complete_count: complete });
  }

  // ─── Task 2: Connect accounting ──────────────────────────────────────

  connectXero(): void { this.connectAccounting('xero'); }
  connectQbo():  void { this.connectAccounting('qbo'); }

  private connectAccounting(type: 'xero' | 'qbo'): void {
    if (this.accountingConnecting) return;
    this.accountingConnecting = true;
    this.analytics.capture('setup_accounting_connect_started', { platform: type });
    const startedAt = Date.now();
    this.organizationService.getIntegrationCredentials(type).subscribe(
      (res) => {
        if (res?.connectURL) {
          // We capture the click time so the OAuth-callback completion event can
          // include round-trip duration; stashed in sessionStorage to survive the
          // off-site redirect.
          sessionStorage.setItem('sd1137:accounting_connect_started_at', String(startedAt));
          sessionStorage.setItem('sd1137:accounting_connect_platform', type);
          window.location.href = type === 'qbo'
            ? this.organizationService.qboConnectUrl(res.connectURL)
            : res.connectURL;
        } else {
          this.accountingConnecting = false;
        }
      },
      () => { this.accountingConnecting = false; }
    );
  }

  // ─── Task 3: Process first bill ──────────────────────────────────────

  triggerUpload(source: 'click' | 'drop'): void {
    this.analytics.capture('setup_upload_zone_clicked', { source });
    // Defer to the dashboard's existing upload sidebar — keeps a single source of
    // truth for the upload UI and document-pipeline handoff.
    this.uploadRequested.emit();
    this.pollForFirstBill();
  }

  installGmailAddOn(): void {
    if (!this.gmailAddOn?.appURL) return;
    this.analytics.capture('setup_capture_install_started', { tool: 'chrome_extension' });
    window.open(this.gmailAddOn.appURL, '_blank', 'noopener');
  }

  installOutlookAddOn(): void {
    if (!this.outlookAddOn?.appURL) return;
    this.analytics.capture('setup_capture_install_started', { tool: 'outlook_addin' });
    window.open(this.outlookAddOn.appURL, '_blank', 'noopener');
  }

  copyForwardingEmail(): void {
    if (!this.forwardingEmail) return;
    navigator.clipboard?.writeText(this.forwardingEmail).then(() => {
      this.copied = true;
      this.analytics.capture('setup_forwarding_address_copied');
      setTimeout(() => this.copied = false, 2000);
    });
  }

  triggerSampleBill(): void {
    this.analytics.capture('setup_sample_bill_triggered');
    // Sample-bill injection isn't wired server-side yet (out of scope for this PR).
    // Open the upload sidebar so the user has an immediate path forward.
    this.uploadRequested.emit();
    this.pollForFirstBill();
  }

  // ─── Drag/drop on upload zone ────────────────────────────────────────

  dragActive = false;
  onDragOver(e: DragEvent): void { e.preventDefault(); this.dragActive = true; }
  onDragLeave(e: DragEvent): void { e.preventDefault(); this.dragActive = false; }
  onDrop(e: DragEvent): void {
    e.preventDefault();
    this.dragActive = false;
    this.triggerUpload('drop');
  }

  // ─── Synced Go modal ────────────────────────────────────────────────

  openGoModal(): void {
    this.goModalOpen = true;
    this.analytics.capture('setup_synced_go_modal_opened');
  }
  closeGoModal(): void {
    this.goModalOpen = false;
  }
  openIosStore(): void {
    this.analytics.capture('setup_synced_go_store_clicked', { platform: 'ios' });
    window.open(this.iosAppUrl, '_blank', 'noopener');
  }
  openAndroidStore(): void {
    this.analytics.capture('setup_synced_go_store_clicked', { platform: 'android' });
    window.open(this.androidAppUrl, '_blank', 'noopener');
  }
}
